import { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { popNavOrigin } from "@/lib/navOrigin";

interface PageHeaderProps {
  title: string;
  onBack?: () => void;
  /** Fallback route if there is no usable history/`from` state. */
  fallbackPath?: string;
  rightAction?: ReactNode;
}

export default function PageHeader({ title, onBack, fallbackPath, rightAction }: PageHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (onBack) return onBack();

    // 1. Explicit `from` state set by the navigator (deterministic).
    const fromState = (location.state as any)?.from;
    if (typeof fromState === "string" && fromState.startsWith("/") && !fromState.startsWith("//")) {
      navigate(fromState);
      return;
    }

    // 2. Per-tab origin stack (survives reloads, ignores referrer pitfalls).
    const popped = popNavOrigin();
    if (popped && popped !== location.pathname + location.search) {
      navigate(popped);
      return;
    }

    // 3. Explicit fallback configured by the page.
    if (fallbackPath) {
      navigate(fallbackPath);
      return;
    }

    // 4. Last resort — browser back, then root.
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
      <Button
        variant="ghost"
        size="icon"
        className="rounded-xl !bg-transparent text-foreground hover:bg-secondary"
        onClick={handleBack}
        aria-label="Back"
      >
        <ArrowLeft className="h-6 w-6" />
      </Button>
      <h1 className="font-display text-lg font-bold flex-1">{title}</h1>
      {rightAction}
    </div>
  );
}
